import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config';
import PasswordField from './PasswordField';

const VendorFieldOfficerManagement = () => {
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [passwordValid, setPasswordValid] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    password: ''
  });

  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  useEffect(() => {
    fetchFieldOfficers();
  }, []);

  const fetchFieldOfficers = async () => {
    try {
      setLoading(true);
      const vendorToken = localStorage.getItem('vendorToken');
      const response = await axios.get(`${API_BASE_URL}/vendor-portal/field-officers`, {
        headers: { Authorization: `Bearer ${vendorToken}` }
      });
      setFieldOfficers(response.data.fieldOfficers || []);
      setError('');
    } catch (err) {
      setError('Failed to load field officers');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (officer = null) => {
    if (officer) {
      setEditingId(officer.id);
      setFormData({ ...officer, password: '' });
      setPasswordValid(true); // editing mode doesn't require password
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        password: ''
      });
      setPasswordValid(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name } = e.target;
    let value = e.target.value;
    if (name === 'phoneNumber') {
      value = value.replace(/[^0-9]/g, '');
    }
    setFormData({
      ...formData,
      [name]: value
    });
    if (e.target.name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(e.target.value && !emailRegex.test(e.target.value) ? 'Invalid email format' : '');
    }
    if (e.target.name === 'phoneNumber') {
      const phoneRegex = /^\d{10}$/;
      setPhoneError(e.target.value && !phoneRegex.test(e.target.value) ? 'Phone must be 10 digits' : '');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const vendorToken = localStorage.getItem('vendorToken');
      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/vendor-portal/field-officers/${editingId}`,
          formData,
          { headers: { Authorization: `Bearer ${vendorToken}` } }
        );
        setSuccess('Field officer updated successfully');
      } else {
        await axios.post(
          `${API_BASE_URL}/vendor-portal/field-officers`,
          formData,
          { headers: { Authorization: `Bearer ${vendorToken}` } }
        );
        setSuccess('Field officer created successfully');
      }
      handleCloseDialog();
      fetchFieldOfficers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save field officer');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (e, officer) => {
    setAnchorEl(e.currentTarget);
    setSelectedOfficer(officer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleOpenDialog(selectedOfficer);
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    try {
      setLoading(true);
      const vendorToken = localStorage.getItem('vendorToken');
      await axios.put(
        `${API_BASE_URL}/vendor-portal/field-officers/${selectedOfficer.id}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${vendorToken}` } }
      );
      setSuccess(`Field officer ${selectedOfficer.status === 'active' ? 'deactivated' : 'activated'}`);
      fetchFieldOfficers();
      handleMenuClose();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to toggle status');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this field officer?')) {
      try {
        setLoading(true);
        const vendorToken = localStorage.getItem('vendorToken');
        await axios.delete(
          `${API_BASE_URL}/vendor-portal/field-officers/${selectedOfficer.id}`,
          { headers: { Authorization: `Bearer ${vendorToken}` } }
        );
        setSuccess('Field officer deleted successfully');
        fetchFieldOfficers();
        handleMenuClose();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Failed to delete field officer');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Field Officers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Field Officer
        </Button>
      </Box>

      {loading && fieldOfficers.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fieldOfficers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No field officers. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                fieldOfficers.map((officer) => (
                  <TableRow key={officer.id} hover>
                    <TableCell>{officer.name}</TableCell>
                    <TableCell>{officer.email}</TableCell>
                    <TableCell>{officer.phoneNumber}</TableCell>
                    <TableCell>
                      <Chip
                        label={officer.status === 'active' ? 'Active' : 'Inactive'}
                        color={officer.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, officer)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          {selectedOfficer?.status === 'active' ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Field Officer' : 'Add Field Officer'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={editingId} // Don't allow email change
              error={!!emailError}
              helperText={emailError || (editingId ? 'Email cannot be changed' : '')}
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              error={!!phoneError}
              helperText={phoneError}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
              onKeyDown={(ev) => {
                const allowed = ['Backspace','Delete','Tab','ArrowLeft','ArrowRight','Home','End'];
                if (allowed.includes(ev.key)) return;
                if (!/^[0-9]$/.test(ev.key)) {
                  ev.preventDefault();
                }
              }}
            />
            <PasswordField
              value={formData.password}
              onChange={handleChange}
              name="password"
              label="Password"
              required={!editingId}
              helperText={editingId ? 'Leave blank to keep current password' : ''}
              email={formData.email}
              editMode={!!editingId}
              showValidation
              onValidityChange={setPasswordValid}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              loading ||
              !formData.name ||
              !formData.email || !!emailError ||
              !formData.phoneNumber || !!phoneError ||
              (!editingId && (!formData.password || !passwordValid)) ||
              (editingId && formData.password && !passwordValid)
            }
          >
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorFieldOfficerManagement;
